'use client';

import { ChevronRight, Package, Search } from "lucide-react";

export interface HeaderSearchResult {
  id: string;
  variantId: string;
  title: string;
  handle: string;
  thumbnail?: string;
  price?: number;
}

interface HeaderSearchDropdownProps {
  suggestions: string[];
  results: HeaderSearchResult[];
  isSearching: boolean;
  onSuggestion: (suggestion: string) => void;
  onResult: (result: HeaderSearchResult) => void;
}

/** Suggestions + live product results under the header search box (shared by desktop and mobile). */
export function HeaderSearchDropdown({ suggestions, results, isSearching, onSuggestion, onResult }: HeaderSearchDropdownProps) {
  if (suggestions.length === 0 && results.length === 0 && !isSearching) return null;

  return (
    <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-hv-line bg-white shadow-[0_18px_40px_-12px_rgba(35,34,36,0.25)] animate-fade-in">
      <div className="max-h-[420px] overflow-y-auto py-1">
        {suggestions.length > 0 && (
          <div>
            <p className="px-4 pb-1 pt-3 font-hv-mono text-[11px] uppercase tracking-[0.04em] text-hv-orange">Suggestions</p>
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSuggestion(suggestion);
                }}
                className="group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-hv-paper"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-hv-card">
                  <Search className="h-3.5 w-3.5 text-hv-soft group-hover:text-hv-orange" />
                </span>
                <span className="text-[15px] font-medium text-hv-ink group-hover:text-hv-orange">{suggestion}</span>
              </button>
            ))}
          </div>
        )}

        {results.length > 0 && (
          <div className={suggestions.length > 0 ? 'mt-1 border-t border-hv-line' : ''}>
            <p className="px-4 pb-1 pt-3 font-hv-mono text-[11px] uppercase tracking-[0.04em] text-hv-orange">
              Products {isSearching && <span className="animate-pulse">•••</span>}
            </p>
            {results.map((result) => (
              <button
                key={`${result.id}-${result.variantId}`}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onResult(result);
                }}
                className="group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-hv-paper"
              >
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-hv-card">
                  {result.thumbnail ? (
                    <img src={result.thumbnail} alt={result.title} width={48} height={48} className="h-full w-full object-cover" />
                  ) : (
                    <Package className="h-5 w-5 text-hv-soft" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-medium text-hv-ink group-hover:text-hv-orange">{result.title}</span>
                  {result.price ? (
                    <span className="mt-0.5 block text-[13px] text-hv-soft">₹{(result.price / 100).toLocaleString('en-IN')}</span>
                  ) : null}
                </span>
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-hv-soft group-hover:text-hv-orange" />
              </button>
            ))}
          </div>
        )}

        {isSearching && results.length === 0 && (
          <div className="px-4 py-8 text-center">
            <div className="mx-auto h-7 w-7 animate-spin rounded-full border-2 border-hv-orange border-t-transparent" />
            <p className="mt-3 text-sm text-hv-soft">Searching…</p>
          </div>
        )}
      </div>
    </div>
  );
}
